/** Stable query keys — keep company-scoped and list/detail separated. */
export const queryKeys = {
  products: {
    all: (companySlug: string) => ["products", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        includeDeactivated?: boolean;
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "products",
        companySlug,
        "list",
        {
          includeDeactivated: Boolean(opts?.includeDeactivated),
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "name",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    detail: (companySlug: string, productId: string) =>
      ["products", companySlug, "detail", productId] as const,
    lots: (companySlug: string, productId: string) =>
      ["products", companySlug, "lots", productId] as const,
  },
  approvals: {
    all: (companySlug: string) => ["approvals", companySlug] as const,
    overrides: (companySlug: string) =>
      ["approvals", companySlug, "overrides"] as const,
    changes: (companySlug: string) =>
      ["approvals", companySlug, "changes"] as const,
    wallet: (companySlug: string) =>
      ["approvals", companySlug, "wallet"] as const,
  },
  inventory: {
    all: (companySlug: string) => ["inventory", companySlug] as const,
    balances: (
      companySlug: string,
      opts?: {
        warehouseId?: string | null;
        q?: string;
        lowStock?: boolean;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "inventory",
        companySlug,
        "balances",
        {
          warehouseId: opts?.warehouseId ?? "all",
          q: opts?.q ?? "",
          lowStock: opts?.lowStock === true,
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "productName",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  parties: {
    all: (companySlug: string) => ["parties", companySlug] as const,
    detail: (companySlug: string, partyId: string) =>
      ["parties", companySlug, "detail", partyId] as const,
    list: (
      companySlug: string,
      opts?: {
        role?: string;
        includeDeactivated?: boolean;
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "parties",
        companySlug,
        "list",
        {
          role: opts?.role ?? "all",
          includeDeactivated: Boolean(opts?.includeDeactivated),
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "name",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  customers: {
    all: (companySlug: string) => ["customers", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        includeDeactivated?: boolean;
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "customers",
        companySlug,
        "list",
        {
          includeDeactivated: Boolean(opts?.includeDeactivated),
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "name",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  suppliers: {
    all: (companySlug: string) => ["suppliers", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        includeDeactivated?: boolean;
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "suppliers",
        companySlug,
        "list",
        {
          includeDeactivated: Boolean(opts?.includeDeactivated),
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "name",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  priceLists: {
    all: (companySlug: string) => ["priceLists", companySlug] as const,
    list: (companySlug: string) =>
      ["priceLists", companySlug, "list"] as const,
    detail: (companySlug: string, listId: string) =>
      ["priceLists", companySlug, "detail", listId] as const,
  },
  payMethods: {
    all: (companySlug: string) => ["payMethods", companySlug] as const,
    list: (companySlug: string) =>
      ["payMethods", companySlug, "list"] as const,
  },
  pos: {
    terminals: (companySlug: string) =>
      ["pos", companySlug, "terminals"] as const,
    tickets: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        terminalId?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "pos",
        companySlug,
        "tickets",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          terminalId: opts?.terminalId ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  deskOpen: {
    cache: (companySlug: string) => ["deskOpen", companySlug, "cache"] as const,
  },
  salesOrders: {
    all: (companySlug: string) => ["salesOrders", companySlug] as const,
    list: (
      companySlug: string,
      filters: {
        tagId: string;
        assigneeFilter: string;
        q?: string;
        lifecycle?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) => ["salesOrders", companySlug, "list", filters] as const,
  },
  salesInvoices: {
    all: (companySlug: string) => ["salesInvoices", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        salesOrderId?: string;
        limit?: number;
        sortDir?: string;
      },
    ) =>
      [
        "salesInvoices",
        companySlug,
        "list",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          salesOrderId: opts?.salesOrderId ?? "",
          limit: opts?.limit ?? null,
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  salesShipments: {
    all: (companySlug: string) => ["salesShipments", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        limit?: number;
        sortDir?: string;
      },
    ) =>
      [
        "salesShipments",
        companySlug,
        "list",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          limit: opts?.limit ?? null,
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    detail: (companySlug: string, shipmentId: string) =>
      ["salesShipments", companySlug, "detail", shipmentId] as const,
  },
  purchaseShipments: {
    all: (companySlug: string) => ["purchaseShipments", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        limit?: number;
        sortDir?: string;
      },
    ) =>
      [
        "purchaseShipments",
        companySlug,
        "list",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          limit: opts?.limit ?? null,
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    detail: (companySlug: string, shipmentId: string) =>
      ["purchaseShipments", companySlug, "detail", shipmentId] as const,
  },
  purchaseInvoices: {
    all: (companySlug: string) => ["purchaseInvoices", companySlug] as const,
    list: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        purchaseOrderId?: string;
        limit?: number;
        sortDir?: string;
      },
    ) =>
      [
        "purchaseInvoices",
        companySlug,
        "list",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          purchaseOrderId: opts?.purchaseOrderId ?? "",
          limit: opts?.limit ?? null,
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  purchaseOrders: {
    all: (companySlug: string) => ["purchaseOrders", companySlug] as const,
    list: (
      companySlug: string,
      filters: {
        tagId: string;
        assigneeFilter: string;
        q?: string;
        lifecycle?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) => ["purchaseOrders", companySlug, "list", filters] as const,
  },
  queues: {
    toCollect: (
      companySlug: string,
      opts?: {
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "queues",
        companySlug,
        "toCollect",
        {
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    toDeliver: (
      companySlug: string,
      opts?: {
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "queues",
        companySlug,
        "toDeliver",
        {
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    toReceive: (
      companySlug: string,
      opts?: {
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "queues",
        companySlug,
        "toReceive",
        {
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    toPay: (
      companySlug: string,
      opts?: {
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "queues",
        companySlug,
        "toPay",
        {
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  returns: {
    all: (companySlug: string) => ["returns", companySlug] as const,
    page: (companySlug: string) =>
      ["returns", companySlug, "page"] as const,
    list: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        limit?: number;
        sourceType?: string;
        sourceId?: string;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "returns",
        companySlug,
        "list",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          limit: opts?.limit ?? null,
          sourceType: opts?.sourceType ?? "",
          sourceId: opts?.sourceId ?? "",
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  payments: {
    customers: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        salesInvoiceId?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "payments",
        companySlug,
        "customers",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          salesInvoiceId: opts?.salesInvoiceId ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    suppliers: (
      companySlug: string,
      opts?: {
        q?: string;
        lifecycle?: string;
        purchaseInvoiceId?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "payments",
        companySlug,
        "suppliers",
        {
          q: opts?.q ?? "",
          lifecycle: opts?.lifecycle ?? "all",
          purchaseInvoiceId: opts?.purchaseInvoiceId ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
  },
  warehouses: {
    all: (companySlug: string) => ["warehouses", companySlug] as const,
    list: (companySlug: string) =>
      ["warehouses", companySlug, "list"] as const,
  },
  branches: {
    all: (companySlug: string) => ["branches", companySlug] as const,
    list: (companySlug: string) =>
      ["branches", companySlug, "list"] as const,
    access: (companySlug: string) =>
      ["branches", companySlug, "access"] as const,
  },
  inbox: {
    list: (companySlug: string) =>
      ["inbox", companySlug, "list"] as const,
  },
  docSeries: {
    all: (companySlug: string) => ["docSeries", companySlug] as const,
  },
  org: {
    settings: (companySlug: string) =>
      ["org", companySlug, "settings"] as const,
    roles: (companySlug: string) => ["org", companySlug, "roles"] as const,
    members: (companySlug: string) =>
      ["org", companySlug, "members"] as const,
  },
  conversions: {
    all: (companySlug: string) => ["conversions", companySlug] as const,
    recipes: (companySlug: string) =>
      ["conversions", companySlug, "recipes"] as const,
    list: (
      companySlug: string,
      opts?: {
        q?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "conversions",
        companySlug,
        "list",
        {
          q: opts?.q ?? "",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "number",
          sortDir: opts?.sortDir ?? "asc",
        },
      ] as const,
    detail: (companySlug: string, conversionId: string) =>
      ["conversions", companySlug, "detail", conversionId] as const,
  },
  journals: {
    detail: (companySlug: string, journalId: string) =>
      ["journals", companySlug, "detail", journalId] as const,
  },
  audit: {
    list: (
      companySlug: string,
      opts?: {
        docType?: string;
        limit?: number;
        sort?: string;
        sortDir?: string;
      },
    ) =>
      [
        "audit",
        companySlug,
        "list",
        {
          docType: opts?.docType || "all",
          limit: opts?.limit ?? null,
          sort: opts?.sort ?? "createdAt",
          sortDir: opts?.sortDir ?? "desc",
        },
      ] as const,
  },
  gl: {
    trialBalance: (companySlug: string) =>
      ["gl", companySlug, "trialBalance"] as const,
    profitLoss: (companySlug: string) =>
      ["gl", companySlug, "profitLoss"] as const,
    accounts: (
      companySlug: string,
      opts?: { includeDeactivated?: boolean },
    ) =>
      [
        "gl",
        companySlug,
        "accounts",
        { includeDeactivated: Boolean(opts?.includeDeactivated) },
      ] as const,
    accountLedger: (companySlug: string, accountId: string) =>
      ["gl", companySlug, "accountLedger", accountId] as const,
  },
};
